import { PaletteColor } from "@brexhq/metal-foundation";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import {
  type DefaultCarouselCardProps,
  type SlideStyles,
} from "@brexhq/next/src/components/modules/CarouselSwipeCards/types";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { buildBynderImageUrl } from "@brexhq/next/src/components/shared/BynderAsset/Image/LegacyImage";
import {
  ResponsiveColoredIcon,
  ResponsiveIcon,
} from "@brexhq/next/src/components/shared/Icon";
import { createButtonStyles } from "@brexhq/next/src/components/shared/styles/Button.elements";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { blocksToText } from "@brexhq/shared/utils/sanity/blocksToText";

const CardButton = styled.span({
  ...createButtonStyles({
    modifier: "secondary",
    size: { LRG: "med", MED: "med", SML: "sml" },
  }),
  marginTop: "auto",
});

const CardPrimaryIcon = styled(Container)<{ cardHasImage: boolean }>(
  ({ theme, cardHasImage }) => {
    return {
      position: "relative",
      height: 50,
      width: 50,
      backgroundColor: theme.colors.background.inverse.primary,
      borderRadius: theme.radius.half,
      // Without a card image we don't need fancy positioning for the icon to overlap it
      // It only needs to sit normally in the card content
      transform: cardHasImage ? "translateY(-50%)" : "initial",
      marginTop: cardHasImage ? -1 * theme.spacing.m32 : 0,
      marginBottom: cardHasImage ? 0 : theme.spacing.m24,
      [theme.breakpoints.SML]: {
        marginTop: cardHasImage ? -1 * theme.spacing.m24 : 0,
        height: 50,
        width: 50,
      },
    };
  },
);

const CardSecondaryIcon = styled.div(({ theme }) => {
  const { spacing, breakpoints } = theme;
  return {
    marginBottom: spacing.m24,
    [breakpoints.SML]: {
      marginBottom: spacing.m24,
    },
  };
});

const CardContent = styled(Container)((props) => {
  const { theme, alignItems } = props;
  const { spacing, breakpoints } = theme;
  return {
    flex: 1,
    textAlign: alignItems === "center" ? "center" : "left",
    padding: spacing.p32,
    height: "100%",
    [breakpoints.SML]: {
      padding: spacing.p24,
    },
  };
});

const CardWrapper = styled(Container)<{
  isLinked: boolean;
  width: SlideStyles;
  margin: SlideStyles;
}>(({ theme, alignItems, isLinked, width, margin }) => {
  const { breakpoints, radius, transitions } = theme;
  const buttonStyles = createButtonStyles({
    modifier: "secondary",
  });
  const buttonHoverStyles = buttonStyles["&:hover"];

  return {
    width: width.LRG,
    minHeight: 265,
    transition: transitions.defaultTransition,
    marginRight: margin.LRG,
    borderRadius: radius.cardRadius,
    overflow: "hidden",
    cursor: isLinked ? "pointer" : "initial",
    textAlign: alignItems === "center" ? "center" : "left",
    "&:hover": isLinked
      ? {
          boxShadow: "6px 12px 36px rgba(46, 46, 46, 0.08)",
          "div[data-name='CardButton']": buttonHoverStyles,
        }
      : {},
    [breakpoints.SML]: {
      width: width.SML,
      minHeight: 272,
      marginRight: margin.SML,
    },
  };
});

const CardHeaderText: React.FC<{
  headerPortableText?: any[];
  textColor: PaletteColor;
  useSmallMargin: boolean;
}> = ({ headerPortableText, useSmallMargin, textColor }) => {
  const { spacing } = useTheme();

  const margin = useSmallMargin ? spacing.m10 : spacing.m24;

  if (!(headerPortableText && headerPortableText.length > 0)) return null;

  return (
    <Text
      as="h3"
      color={textColor}
      components={{
        LRG: "Headline4",
        MED: "Headline4",
        SML: "Subtitle1",
      }}
      margin={{
        LRG: [0, 0, margin, 0],
        MED: [0, 0, margin, 0],
        SML: [0, 0, margin, 0],
      }}
    >
      {blocksToText(headerPortableText)}
    </Text>
  );
};

const CardBodyText: React.FC<{
  bodyPortableText?: any[];
  textColor: PaletteColor;
}> = ({ bodyPortableText, textColor }) => {
  const { spacing } = useTheme();

  if (!(bodyPortableText && bodyPortableText.length > 0)) return null;

  const content = blocksToText(bodyPortableText);

  return (
    <Text
      as="h3"
      color={textColor}
      components={{
        LRG: "Subtitle2",
        MED: "Subtitle2",
        SML: "Body2",
      }}
      margin={{
        LRG: [0, 0, spacing.m32, 0],
        MED: [0, 0, spacing.m32, 0],
        SML: [0, 0, spacing.m24, 0],
      }}
    >
      {content}
    </Text>
  );
};

const CardMedia = styled("img")(({ theme }) => {
  return {
    objectFit: "cover",
    height: 254,
    width: "100%",
    pointerEvents: "none",
    [theme.breakpoints.SML]: {
      height: 174,
    },
  };
});

export const defaultCardRenderer: (
  props: DefaultCarouselCardProps,
) => React.ReactNode = (props) => <SwipeCard {...props} />;

/**
 *
 * default view of a carousel card
 */
export const SwipeCard = ({
  alignment,
  moduleTheme,
  secondaryIcon,
  headerPortableText,
  bodyPortableText,
  image,
  buttonText,
  cardUrl,
  primaryIcon,
  width,
  margin,
  cardTheme,
}: DefaultCarouselCardProps) => {
  const { card } = getModuleThemeColors(moduleTheme);
  const { standard } = getModuleThemeColors(cardTheme ?? "PrimaryWhite");
  const actualTheme = cardTheme ? standard : card;

  return (
    <CardWrapper
      height="100%"
      display="flex"
      direction="column"
      backgroundColor={actualTheme.background}
      isLinked={!!cardUrl}
      alignItems={alignment === "center" ? "center" : undefined}
      justifyContent="flex-start"
      width={width}
      margin={margin as any}
    >
      {image?.image && (
        <CardMedia
          src={buildBynderImageUrl(image.image, { width: 600 })}
          alt={image.image.description ?? image.image.name}
        />
      )}
      <CardContent
        display="flex"
        direction="column"
        alignItems={alignment === "center" ? "center" : undefined}
        justifyContent="flex-start"
      >
        {primaryIcon && (
          <CardPrimaryIcon
            display="flex"
            direction="column"
            justifyContent="center"
            alignItems="center"
            cardHasImage={!!image?.image}
          >
            <ResponsiveColoredIcon
              responsive={{
                LRG: { width: 24 },
                MED: { width: 24 },
                SML: { width: 24 },
              }}
              icon={primaryIcon}
            />
          </CardPrimaryIcon>
        )}
        {secondaryIcon && (
          <CardSecondaryIcon>
            <ResponsiveIcon
              icon={secondaryIcon}
              color={actualTheme.icons}
              responsive={{
                LRG: {
                  height: 32,
                },
                MED: {
                  height: 32,
                },
                SML: {
                  height: 32,
                },
              }}
            />
          </CardSecondaryIcon>
        )}
        <CardHeaderText
          headerPortableText={headerPortableText}
          textColor={actualTheme.textPrimary}
          useSmallMargin={Boolean(
            bodyPortableText && bodyPortableText.length > 0,
          )}
        />
        <CardBodyText
          bodyPortableText={bodyPortableText}
          textColor={actualTheme.textPrimary}
        />
        {buttonText && !!cardUrl && (
          <CardButton tabIndex={-1} data-name="CardButton">
            {buttonText}
          </CardButton>
        )}
      </CardContent>
    </CardWrapper>
  );
};
